import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { api, SUB_ROLES } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Sun, Moon } from "lucide-react";

export default function Profile() {
  const { user, refresh } = useAuth();
  const { theme, setTheme } = useTheme();
  const [role, setRole] = useState(user?.role || "wholesaler");
  const [subRole, setSubRole] = useState(user?.sub_role || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [open, setOpen] = useState(user?.open_to_partner ?? true);
  const [logPersonal, setLogPersonal] = useState(user?.log_personal_kpis ?? false);
  const [teamName, setTeamName] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [saving, setSaving] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const [consent, setConsent] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { role, bio, open_to_partner: open, log_personal_kpis: logPersonal };
      if (subRole) payload.sub_role = subRole;
      await api.post("/profile", payload);
      toast.success("Profile saved");
      refresh();
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const createTeam = async () => {
    if (!teamName.trim()) return;
    try {
      await api.post("/profile", { role: "team_leader", team_name: teamName });
      toast.success("Team created — you are now a Team Leader");
      setTeamName("");
      refresh();
    } catch { toast.error("Failed"); }
  };

  const requestJoin = () => {
    if (!joinCode.trim()) return;
    setConsent(false);
    setConsentOpen(true);
  };

  const confirmJoin = async () => {
    try {
      await api.post("/profile", { join_team_code: joinCode, team_authority_consent: true });
      toast.success("Joined team");
      setJoinCode("");
      setConsentOpen(false);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-8 md:p-10 max-w-3xl">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-widest text-[#39FF14] mb-2">// Profile</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight">Your setup</h1>
      </div>

      {/* Appearance / theme toggle */}
      <div className="hairline bg-[#0F0F0F] p-6 mb-3">
        <h2 className="font-display text-sm font-bold uppercase tracking-widest mb-4">Appearance</h2>
        <div className="text-xs text-neutral-500 mb-3">Choose the interface theme. Saved on this device.</div>
        <div className="grid grid-cols-2 gap-3">
          <button
            data-testid="theme-dark-btn"
            onClick={() => setTheme("dark")}
            className={`hairline p-5 text-left transition-all ${
              theme === "dark"
                ? "border-[#39FF14] bg-[#0A0A0A] ring-1 ring-[#39FF14]"
                : "bg-[#0F0F0F] hover:bg-[#141414]"
            }`}
          >
            <div className="flex items-center gap-2 mb-3">
              <Moon size={16} className={theme === "dark" ? "text-[#39FF14]" : "text-neutral-500"} />
              <span className="font-display text-sm font-bold uppercase tracking-widest">Dark</span>
              {theme === "dark" && (
                <span className="ml-auto text-[9px] uppercase tracking-widest text-[#39FF14]">Active</span>
              )}
            </div>
            <div className="flex gap-1.5">
              <div className="w-8 h-8 bg-[#0A0A0A] border border-neutral-800" />
              <div className="w-8 h-8 bg-[#0F0F0F] border border-neutral-800" />
              <div className="w-8 h-8 bg-[#39FF14]" />
            </div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-3">
              Default · obsidian + neon
            </div>
          </button>

          <button
            data-testid="theme-light-btn"
            onClick={() => setTheme("light")}
            className={`hairline p-5 text-left transition-all ${
              theme === "light"
                ? "border-[#39FF14] ring-1 ring-[#39FF14]"
                : "bg-[#0F0F0F] hover:bg-[#141414]"
            }`}
            style={theme === "light" ? { background: "#FFFFFF" } : {}}
          >
            <div className="flex items-center gap-2 mb-3">
              <Sun size={16} className={theme === "light" ? "text-[#16A34A]" : "text-neutral-500"} />
              <span className={`font-display text-sm font-bold uppercase tracking-widest ${
                theme === "light" ? "text-neutral-900" : ""
              }`}>Light</span>
              {theme === "light" && (
                <span className="ml-auto text-[9px] uppercase tracking-widest text-[#16A34A]">Active</span>
              )}
            </div>
            <div className="flex gap-1.5">
              <div className="w-8 h-8 border" style={{ background: "#FFFFFF", borderColor: "#D4D4D8" }} />
              <div className="w-8 h-8 border" style={{ background: "#F4F4F5", borderColor: "#D4D4D8" }} />
              <div className="w-8 h-8 bg-[#39FF14]" />
            </div>
            <div className={`text-[10px] uppercase tracking-widest mt-3 ${
              theme === "light" ? "text-neutral-500" : "text-neutral-500"
            }`}>
              Bright · easy on the eyes in daylight
            </div>
          </button>
        </div>
      </div>

      <div className="hairline bg-[#0F0F0F] p-6 mb-3">
        <h2 className="font-display text-sm font-bold uppercase tracking-widest mb-4">Identity</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1.5">Role</label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="role-select" className="rounded-none bg-[#0A0A0A] hairline">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-[#0F0F0F] hairline">
                <SelectItem value="wholesaler">Wholesaler</SelectItem>
                <SelectItem value="team_leader">Team Leader</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1.5">Wholesaling role</label>
            <Select value={subRole} onValueChange={setSubRole}>
              <SelectTrigger data-testid="subrole-select" className="rounded-none bg-[#0A0A0A] hairline">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-[#0F0F0F] hairline">
                {SUB_ROLES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4">
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1.5">Bio (shown to potential partners)</label>
          <Textarea
            data-testid="bio-input"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            className="rounded-none bg-[#0A0A0A] hairline min-h-[80px]"
            placeholder="Markets, strengths, what you're looking for in a partner…"
          />
        </div>
        <div className="mt-4 flex items-center justify-between hairline bg-[#0A0A0A] p-4">
          <div>
            <div className="font-semibold text-sm">Open to accountability partners</div>
            <div className="text-xs text-neutral-500">Toggle off to hide from Browse.</div>
          </div>
          <Switch data-testid="open-toggle" checked={open} onCheckedChange={setOpen} />
        </div>
        {role === "team_leader" && (
          <div className="mt-3 flex items-center justify-between hairline bg-[#0A0A0A] p-4">
            <div>
              <div className="font-semibold text-sm">Log personal KPIs too</div>
              <div className="text-xs text-neutral-500">Adds a second Wholesaler KPI card to your dashboard for your own outreach work.</div>
            </div>
            <Switch data-testid="log-personal-toggle" checked={logPersonal} onCheckedChange={setLogPersonal} />
          </div>
        )}
        <div className="mt-5 flex justify-end">
          <Button
            data-testid="save-profile-btn"
            onClick={save}
            disabled={saving}
            className="rounded-none bg-[#39FF14] text-black hover:bg-white h-10 text-xs uppercase tracking-widest px-6"
          >
            {saving ? "Saving…" : "Save profile"}
          </Button>
        </div>
      </div>

      <div className="hairline bg-[#0F0F0F] p-6">
        <h2 className="font-display text-sm font-bold uppercase tracking-widest mb-4">Team</h2>
        {user?.team_id ? (
          <div className="text-sm text-neutral-400">You are on a team. Head to <span className="text-[#39FF14]">My Team</span>.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-2">Create a team (as Leader)</label>
              <div className="flex gap-2">
                <Input
                  data-testid="team-name-input"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder="Team name"
                  className="rounded-none bg-[#0A0A0A] hairline"
                />
                <Button
                  data-testid="create-team-btn"
                  onClick={createTeam}
                  className="rounded-none bg-[#39FF14] text-black hover:bg-white h-10 text-xs uppercase tracking-widest px-4"
                >
                  Create
                </Button>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-2">Join with code</label>
              <div className="flex gap-2">
                <Input
                  data-testid="join-code-input"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  placeholder="ABC123"
                  className="rounded-none bg-[#0A0A0A] hairline font-mono uppercase"
                />
                <Button
                  data-testid="join-team-btn"
                  onClick={requestJoin}
                  variant="outline"
                  className="rounded-none hairline bg-transparent h-10 text-xs uppercase tracking-widest px-4"
                >
                  Join
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Consent modal — required to join a team */}
      <Dialog open={consentOpen} onOpenChange={setConsentOpen}>
        <DialogContent data-testid="consent-dialog" className="rounded-none bg-[#0F0F0F] hairline max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-widest text-sm flex items-center gap-2">
              <AlertTriangle size={16} className="text-yellow-400" /> Team leader authority
            </DialogTitle>
            <DialogDescription className="text-xs text-neutral-500">
              Before joining, please read and acknowledge how team leadership works on this platform.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-neutral-300 space-y-3">
            <p>By joining team <span className="text-[#39FF14] font-mono">{joinCode}</span>, you consent that its Team Leader may:</p>
            <ul className="text-xs text-neutral-400 space-y-1.5 list-disc list-inside pl-2">
              <li>View your daily KPI activity and performance history</li>
              <li>See a "traffic light" status (green / yellow / red) of your workdays</li>
              <li>Contact you for a performance review after 3 consecutive missed workdays</li>
              <li><strong className="text-yellow-400">Suspend</strong> your KPI logging access if you miss 5 consecutive workdays</li>
              <li><strong className="text-red-400">Terminate</strong> your access to the team at their discretion</li>
            </ul>
            <p className="text-xs text-neutral-500">You may leave the team at any time. Your personal KPI history remains yours.</p>
            <label className="flex items-start gap-2 pt-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="consent-checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 accent-[#39FF14]"
              />
              <span className="text-xs">I understand and agree to the team leader's authority as described above.</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConsentOpen(false)} className="rounded-none hairline bg-transparent">Cancel</Button>
            <Button
              data-testid="consent-confirm"
              onClick={confirmJoin}
              disabled={!consent}
              className="rounded-none bg-[#39FF14] text-black hover:bg-white disabled:opacity-40 uppercase tracking-widest text-xs"
            >Join team</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
